import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { message } from 'antd';

import { activityLogsService, type ActivityLog, type ActivityLogsFilters } from '@/services/activity-logs.service';
import { fetchImageAsDataUri } from '@/contexts/AvatarContext';

/**
 * Interface kết quả trả về từ hook useActivityLogs
 * Tách toàn bộ logic nghiệp vụ của trang nhật ký hoạt động ra khỏi layer Page.
 */
export interface UseActivityLogsResult {
  // data
  logs: ActivityLog[];
  loading: boolean;
  error: string | null;
  total: number;

  // pagination
  currentPage: number;
  pageSize: number;
  handlePageChange: (page: number, size?: number) => void;
  handlePageSizeChange: (current: number, size: number) => void;

  // filters
  searchText: string;
  setSearchText: (value: string) => void;
  selectedResourceType: string;
  setSelectedResourceType: (value: string) => void;
  selectedResult: string;
  setSelectedResult: (value: string) => void;
  dateRange: [Dayjs | null, Dayjs | null] | null;
  setDateRange: (value: [Dayjs | null, Dayjs | null] | null) => void;
  isFilterActive: boolean;
  handleClearFilters: () => void;

  // selection / modal
  selectedLog: ActivityLog | null;
  isDetailModalOpen: boolean;
  handleViewDetail: (log: ActivityLog) => void;
  handleCloseDetailModal: () => void;

  // avatar cache & loader
  avatarCacheRef: React.MutableRefObject<Map<string, string>>;
  fetchAvatarAsDataUri: (imageUrl: string) => Promise<string | null>;
}

/**
 * Hook debounce nhẹ cho input search:
 * - Giảm số lần gọi API khi người dùng gõ nhanh
 * - UI vẫn phản hồi tức thì (giữ `value` riêng)
 */
function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delayMs]);

  return debouncedValue;
}

/**
 * Hook domain: quản lý toàn bộ logic nghiệp vụ của trang nhật ký hoạt động
 * - State filters, phân trang, dữ liệu
 * - Build params gọi API
 * - Xử lý debounce search, tính toán isFilterActive
 * - Quản lý modal chi tiết
 * - Tối ưu fetch avatar với cache
 */
export const useActivityLogs = (): UseActivityLogsResult => {
  /**
   * Dữ liệu hiển thị bảng.
   */
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  /**
   * Modal chi tiết.
   */
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  /**
   * Bộ lọc.
   */
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebouncedValue(searchText, 200);
  const [selectedResourceType, setSelectedResourceType] = useState<string>('Tất cả');
  const [selectedResult, setSelectedResult] = useState<string>('Tất cả');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  /**
   * Cache avatar (dataURI) để tránh fetch lại khi đổi trang/filter.
   */
  const avatarCacheRef = useRef<Map<string, string>>(new Map());

  /**
   * Dùng để bỏ qua response cũ khi người dùng đổi filter liên tục (giảm lag/nhấp nháy).
   */
  const lastRequestIdRef = useRef(0);

  /**
   * Khi search thay đổi (sau debounce), tự reset về trang 1 để UX đúng.
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  /**
   * Tìm action code từ text tìm kiếm (có thể là tiếng Việt hoặc action code).
   */
  const findActionCode = useCallback((text: string): string | null => {
    if (!text.trim()) return null;

    const searchLower = text.toLowerCase().trim();

    // Mapping tạm thời, backend xử lý OR giữa action và userSearch
    const actionLabels: Record<string, string> = {
      LOGIN_SUCCESS: 'Đăng nhập thành công',
      LOGIN_FAILED: 'Đăng nhập thất bại',
      VIEW_ACTIVITY_LOGS: 'Xem nhật ký hoạt động',
      VIEW_ACTIVITY_LOGS_STATS: 'Xem thống kê nhật ký hoạt động',
      VIEW_LOGIN_LOGS: 'Xem nhật ký đăng nhập',
      VIEW_RESOURCE_LOGS: 'Xem nhật ký tài nguyên',
      UPDATE_USER: 'Cập nhật người dùng',
      DELETE_USER: 'Xóa người dùng',
      CREATE_USER: 'Tạo người dùng',
      CREATE_COMPANY: 'Tạo công ty',
      CREATE_GPS_LOCATION: 'Tạo vị trí GPS',
      CREATE_WORK_SHIFT: 'Tạo ca làm',
      CREATE_ATTENDANCE_MANUAL: 'Tạo chấm công thủ công',
      CHANGE_PASSWORD: 'Đổi mật khẩu',
      VIEW_USER_PROFILE: 'Xem hồ sơ người dùng',
      BLOCK_USER: 'Khóa người dùng',
      UNBLOCK_USER: 'Mở khóa người dùng',
      PERMANENT_DELETE_USER: 'Xóa vĩnh viễn người dùng',
      RESTORE_USER: 'Khôi phục người dùng',
      REQUEST_USER_UPDATE: 'Yêu cầu cập nhật thông tin',
      APPROVE_USER_UPDATE: 'Phê duyệt cập nhật',
      REJECT_USER_UPDATE: 'Từ chối cập nhật',
      UPDATE_GPS_LOCATION: 'Cập nhật vị trí GPS',
      UPDATE_WORK_SHIFT: 'Cập nhật ca làm',
      ACTIVATE_WORK_SHIFT: 'Kích hoạt ca làm',
      UPDATE_COMPANY: 'Cập nhật công ty',
      UPDATE_ATTENDANCE: 'Cập nhật chấm công',
      UPDATE_USER_AVATAR: 'Cập nhật ảnh đại diện',
      UPDATE_MEAL_ALLOWANCE_CONFIG: 'Cập nhật cấu hình phụ cấp cơm',
    };

    for (const [actionCode, vietnameseLabel] of Object.entries(actionLabels)) {
      if (vietnameseLabel.toLowerCase().includes(searchLower)) {
        return actionCode;
      }
    }

    if (actionLabels[text.toUpperCase()]) {
      return text.toUpperCase();
    }

    return null;
  }, []);

  /**
   * Build filters một lần (giảm logic lặp + giảm sai lệch dependency).
   */
  const apiFilters: ActivityLogsFilters = useMemo(() => {
    const filters: ActivityLogsFilters = { page: currentPage, limit: pageSize };

    // Search: vừa tìm action (mapping) vừa tìm theo nhân sự (backend OR)
    if (debouncedSearchText) {
      const actionCode = findActionCode(debouncedSearchText);
      filters.action = actionCode || debouncedSearchText;
      filters.userSearch = debouncedSearchText;
    }

    if (selectedResourceType !== 'Tất cả') filters.resourceType = selectedResourceType as any;
    if (selectedResult !== 'Tất cả') filters.result = selectedResult as any;

    if (dateRange?.[0] && dateRange?.[1]) {
      filters.startDate = dateRange[0].format('YYYY-MM-DD');
      filters.endDate = dateRange[1].format('YYYY-MM-DD');
    }

    return filters;
  }, [currentPage, pageSize, debouncedSearchText, selectedResourceType, selectedResult, dateRange, findActionCode]);

  /**
   * Fetch logs từ API.
   * Tối ưu: bỏ qua response cũ nếu filter/page đổi quá nhanh.
   */
  const fetchLogs = useCallback(async () => {
    const requestId = ++lastRequestIdRef.current;
    try {
      setLoading(true);
      const response = await activityLogsService.getActivityLogs(apiFilters);

      // Nếu đã có request mới hơn -> bỏ qua response này (tránh lag/nhấp nháy)
      if (requestId !== lastRequestIdRef.current) return;

      setLogs(Array.isArray(response?.data) ? response.data : []);
      setTotal(response?.total ?? 0);
      setError(null);
    } catch (err) {
      // Nếu đã có request mới hơn -> không show lỗi của request cũ
      if (requestId !== lastRequestIdRef.current) return;

      setLogs([]);
      setTotal(0);
      const errorMessage = 'Không thể tải nhật ký hoạt động. Vui lòng thử lại sau.';
      setError(errorMessage);
      message.error(errorMessage);
      // Có thể bổ sung handleApiError nếu project sử dụng
    } finally {
      if (requestId === lastRequestIdRef.current) setLoading(false);
    }
  }, [apiFilters]);

  /**
   * Tự động fetch logs khi component mount hoặc khi filters thay đổi
   */
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /**
   * Xóa tất cả filters và reset về trạng thái ban đầu
   */
  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setSelectedResourceType('Tất cả');
    setSelectedResult('Tất cả');
    setDateRange(null);
    setCurrentPage(1);
  }, []);

  /**
   * Kiểm tra xem có filter nào đang được áp dụng không
   */
  const isFilterActive = useMemo(() => {
    return (
      Boolean(searchText) ||
      (selectedResourceType && selectedResourceType !== 'Tất cả') ||
      (selectedResult && selectedResult !== 'Tất cả') ||
      Boolean(dateRange && dateRange[0] && dateRange[1])
    );
  }, [searchText, selectedResourceType, selectedResult, dateRange]);

  /**
   * Mở modal chi tiết khi click vào nút xem chi tiết
   */
  const handleViewDetail = useCallback((log: ActivityLog) => {
    setSelectedLog(log);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
  }, []);

  /**
   * Fetch avatar và convert sang data URI với cache
   * Kiểm tra cache trước, nếu không có thì fetch và cache lại
   * imageUrl đã được buildFullUrl() trước đó, nên chỉ cần fetch trực tiếp
   */
  const fetchAvatarAsDataUri = useCallback(
    async (imageUrl: string): Promise<string | null> => {
      if (avatarCacheRef.current.has(imageUrl)) {
        return avatarCacheRef.current.get(imageUrl) || null;
      }

      const dataUri = await fetchImageAsDataUri(imageUrl);

      if (dataUri) {
        avatarCacheRef.current.set(imageUrl, dataUri);
      }

      return dataUri;
    },
    [],
  );

  const handlePageChange = useCallback(
    (page: number, size?: number) => {
      setCurrentPage(page);
      if (size) setPageSize(size);
    },
    [],
  );

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  return {
    logs,
    loading,
    error,
    total,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    searchText,
    setSearchText,
    selectedResourceType,
    setSelectedResourceType,
    selectedResult,
    setSelectedResult,
    dateRange,
    setDateRange,
    isFilterActive,
    handleClearFilters,
    selectedLog,
    isDetailModalOpen,
    handleViewDetail,
    handleCloseDetailModal,
    avatarCacheRef,
    fetchAvatarAsDataUri,
  };
};


